import type { ZoneRecord } from '../../../types/models/Zone';
import type { ApiResponseDnsRrsetWrite, RecordContentError } from '../../../types/api/ApiResponseDomainName';

/** Parsed SOA fields. PowerDNS returns SOA content as a single space-joined string:
 *  "<primaryNs> <hostmaster> <serial> <refresh> <retry> <expire> <negativeTtl>". */
export interface ParsedSoa {
  primaryNs: string;
  hostmaster: string;
  serial: number;
  refresh: number;
  retry: number;
  expire: number;
  negativeTtl: number;
  /** The original, unparsed content string (shown as a fallback / for reference). */
  raw: string;
}

/** The SOA RRSet from a zone's records (type === 'SOA'), or null when absent. */
export function findSoaRecord(records: ZoneRecord[]): ZoneRecord | null {
  return records.find((r) => r.type.toUpperCase() === 'SOA') ?? null;
}

/** Parse an SOA RRSet's first content string into its 7 fields. Returns null when the
 *  record is missing or does not have exactly 7 whitespace-separated tokens (so the UI
 *  can fall back to showing the raw content). Numeric fields use Number(); a token that
 *  is not a finite number yields null (treated as unparseable). */
export function parseSoa(soa: ZoneRecord | null): ParsedSoa | null {
  if (soa === null || soa.content.length === 0) {
    return null;
  }
  const raw = soa.content[0];
  const parts = raw.trim().split(/\s+/);
  if (parts.length !== 7) {
    return null;
  }
  const [primaryNs, hostmaster, serialS, refreshS, retryS, expireS, negTtlS] = parts;
  const nums = [serialS, refreshS, retryS, expireS, negTtlS].map(Number);
  if (nums.some((n) => !Number.isFinite(n))) {
    return null;
  }
  const [serial, refresh, retry, expire, negativeTtl] = nums;
  return { primaryNs, hostmaster, serial, refresh, retry, expire, negativeTtl, raw };
}

/**
 * Semantic display order for DNS record types (top → bottom). SOA is intentionally
 * absent — it is shown in the SOA/Zone card, never in the records table. Any type not
 * listed here sorts AFTER all listed types, alphabetically.
 */
export const RECORD_TYPE_ORDER: readonly string[] = [
  'NS',
  'A',
  'AAAA',
  'CNAME',
  'MX',
  'TXT',
  'SRV',
  'CAA',
  'NAPTR',
  'PTR',
  'SSHFP',
  'TLSA',
  'LOC',
];

/** Rank of a type within RECORD_TYPE_ORDER; unlisted types rank last (array length). */
export function semanticTypeRank(type: string): number {
  const idx = RECORD_TYPE_ORDER.indexOf(type.toUpperCase());
  return idx === -1 ? RECORD_TYPE_ORDER.length : idx;
}

/**
 * Order two record types for display: by semantic rank, then alphabetically (so multiple
 * unlisted types are still deterministically ordered after the known ones).
 */
export function compareRecordType(a: string, b: string): number {
  const ra = semanticTypeRank(a);
  const rb = semanticTypeRank(b);
  if (ra !== rb) {
    return ra - rb;
  }
  return a.toUpperCase().localeCompare(b.toUpperCase(), undefined, { sensitivity: 'base' });
}

/** True when an RRSet name equals the zone apex (trailing dots / case ignored). */
export function isApexName(name: string, zoneApex: string): boolean {
  const n = name.replace(/\.$/, '').toLowerCase();
  const z = zoneApex.replace(/\.$/, '').toLowerCase();
  return n === z;
}

/** The distinct record types present (excluding SOA), in semantic order, for the filter. */
export function presentRecordTypes(records: ZoneRecord[]): string[] {
  const set = new Set<string>();
  for (const r of records) {
    const t = r.type.toUpperCase();
    if (t !== 'SOA') {
      set.add(t);
    }
  }
  return Array.from(set).sort(compareRecordType);
}

/**
 * Table rows derived from a zone's records:
 *  - SOA excluded (shown in the card).
 *  - Optional case-insensitive type filter (exact match on upper-cased type).
 *  - Optional search across the DISPLAYED name (per `view`) + joined content
 *    (case-insensitive substring) — so search reflects what the user sees.
 *  - Sorted by SEMANTIC TYPE ORDER, then — within NS only — apex first, then name asc.
 *  Input is not mutated.
 */
export function buildRecordRows(
  records: ZoneRecord[],
  zoneApex: string,
  typeFilter: string | null,
  search: string,
  view: DnsNameView,
): ZoneRecord[] {
  const q = search.trim().toLowerCase();
  const tf = typeFilter?.toUpperCase() ?? null;

  const rows = records.filter((r) => {
    const type = r.type.toUpperCase();
    if (type === 'SOA') {
      return false;
    }
    if (tf !== null && type !== tf) {
      return false;
    }
    if (q) {
      // Match the name AS DISPLAYED (relative => '@'/stripped, absolute => FQDN) + content,
      // so search reflects what the user sees. Fixes relative view, where the apex substring
      // — contained in every in-zone FQDN — would otherwise match every record.
      const visibleName = displayRecordName(r.name, zoneApex, view);
      const haystack = `${visibleName} ${r.content.join(' ')}`.toLowerCase();
      if (!haystack.includes(q)) {
        return false;
      }
    }
    return true;
  });

  return [...rows].sort((a, b) => {
    const byType = compareRecordType(a.type, b.type);
    if (byType !== 0) {
      return byType;
    }
    // Same type. For NS, pin the apex record first.
    if (a.type.toUpperCase() === 'NS') {
      const aApex = isApexName(a.name, zoneApex);
      const bApex = isApexName(b.name, zoneApex);
      if (aApex !== bApex) {
        return aApex ? -1 : 1;
      }
    }
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

export interface RecordTypeGroup {
  type: string;
  records: ZoneRecord[];
}

/**
 * Split an ALREADY-SORTED record list into contiguous same-type segments, preserving
 * order. buildRecordRows groups types contiguously, so applied to a page slice this
 * yields the per-type groups for that page (a type spanning a page boundary therefore
 * gets a header on each page — intended).
 */
export function groupRecordsByType(rows: ZoneRecord[]): RecordTypeGroup[] {
  const groups: RecordTypeGroup[] = [];
  for (const r of rows) {
    const type = r.type.toUpperCase();
    const last = groups[groups.length - 1];
    if (last !== undefined && last.type === type) {
      last.records.push(r);
    } else {
      groups.push({ type, records: [r] });
    }
  }
  return groups;
}

/** Count records per (upper-cased) type — for group-header counts over the full filtered set. */
export function countByType(rows: ZoneRecord[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const t = r.type.toUpperCase();
    counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return counts;
}

/** How record names are displayed in the records table. */
export type DnsNameView = 'relative' | 'absolute';

/**
 * Render a record's name for the chosen view.
 *  - 'absolute': the canonical FQDN as stored (trailing dot preserved) — current behaviour.
 *  - 'relative': BIND-style — the apex itself becomes '@'; an in-zone name has the apex
 *    suffix stripped (case of the remaining label preserved); a name not within the apex
 *    falls back to the absolute form (defensive).
 */
export function displayRecordName(name: string, zoneApex: string, view: DnsNameView): string {
  if (view === 'absolute') {
    return name;
  }
  const nameNoDot = name.replace(/\.$/, '');
  const apexNoDot = zoneApex.replace(/\.$/, '');
  if (nameNoDot.toLowerCase() === apexNoDot.toLowerCase()) {
    return '@';
  }
  const suffix = `.${apexNoDot.toLowerCase()}`;
  if (nameNoDot.toLowerCase().endsWith(suffix)) {
    return nameNoDot.slice(0, nameNoDot.length - apexNoDot.length - 1);
  }
  return name;
}

/**
 * RRSet types the write UI offers (create type selector) and treats as editable (Step 6 gates the
 * rest as "coming soon"). Mirrors the backend's WRITABLE_RRSET_TYPES. NS is included but the apex
 * NS set is blocked separately (structural). Ordered via RECORD_TYPE_ORDER for the selector.
 */
export const WRITABLE_RECORD_TYPES: readonly string[] = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV', 'CAA', 'NS'];

/**
 * Inverse of displayRecordName('…','relative'): turn a user-entered relative name + the zone apex
 * into the canonical FQDN the API expects (single trailing dot). '@' or '' → the apex itself. A name
 * the user already typed in full (ending in the apex, with or without a trailing dot) is not
 * double-suffixed. Case of the entered label is preserved.
 */
export function toFqdn(input: string, zoneApex: string): string {
  const apexNoDot = zoneApex.replace(/\.$/, '');
  const trimmed = input.trim();
  if (trimmed === '' || trimmed === '@') {
    return `${apexNoDot}.`;
  }
  const noDot = trimmed.replace(/\.$/, '');
  if (noDot.toLowerCase() === apexNoDot.toLowerCase()) {
    return `${apexNoDot}.`;
  }
  if (noDot.toLowerCase().endsWith(`.${apexNoDot.toLowerCase()}`)) {
    return `${noDot}.`;
  }
  return `${noDot}.${apexNoDot}.`;
}

/**
 * Pull the most specific message out of a failed rrset write (POST/PATCH …/dns/rrset). The backend
 * returns failures as JSend `error` at HTTP 200 with a field→error map under `data` — `content` can be a
 * single body-shape string OR a list of per-line RecordContentError — so this collapses that to one
 * human-readable string, falling back to the top-level `message`. Shared by the SPF/DMARC wizards and the
 * DKIM status cell's "Fix" action so the extraction logic lives in exactly one place.
 */
export function rrsetErrorMessage(body: ApiResponseDnsRrsetWrite | null): string | null {
  const d = body?.data;
  if (d) {
    if (typeof d.content === 'string') return d.content;
    if (Array.isArray(d.content) && d.content.length > 0) {
      return (d.content as RecordContentError[]).map((e) => e.message).join('; ');
    }
    if (typeof d.name === 'string') return d.name;
    if (typeof d.type === 'string') return d.type;
    if (typeof d.ttl === 'string') return d.ttl;
  }
  return body?.message ?? null;
}
